import { useCallback, useEffect, useRef, useState } from 'react';

interface UseDesktopHoverPlayOptions {
  /**
   * Whether hover tracking applies at all. Off when something else owns the reveal — on a hoverless
   * device that is the centre line, which nominates a row by scroll position instead.
   */
  enabled: boolean;
  /**
   * Temporarily suspend handlers without disabling. Typical use:
   * when a modal is open on top of the card. Handlers no-op while
   * suspended; returns to normal once false.
   */
  suspended: boolean;
}

/**
 * Desktop hover state for a library grid card. Owns `isHovered`
 * (for overlay visibility) and `isFadingOut` (for the overlay
 * fade-out transition).
 *
 * Playback is intentionally NOT managed here. When the
 * `Autoplay videos` setting is on, `VideoPreview` receives
 * `autoPlay=true` and handles its own hover-triggered play/pause.
 * When the setting is off, `autoPlay` is false and `VideoPreview`'s
 * hover handler is inactive, so hover shows overlays without
 * playing the video.
 *
 * The previous version of this hook also toggled a `playVideo` prop
 * on hover, which caused `VideoPreview`'s HLS init effect to tear
 * down and rebuild on every hover (since `playVideo` was one of its
 * deps). That made the video reload from scratch and visibly
 * "stutter" when hovered. Driving playback via `autoPlay` alone
 * avoids the HLS churn.
 */
export function useDesktopHoverPlay({ enabled, suspended }: UseDesktopHoverPlayOptions) {
  const [isHovered, setIsHovered] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);
  const fadeOutTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearFadeOutTimer = () => {
    if (fadeOutTimer.current) {
      clearTimeout(fadeOutTimer.current);
      fadeOutTimer.current = null;
    }
  };

  // Cancel any pending fade-out when the component unmounts so the
  // timeout doesn't try to setState on an unmounted component.
  useEffect(() => clearFadeOutTimer, []);

  const skip = !enabled || suspended;

  /**
   * Pointer events, not mouse events, so a finger can be told apart from a cursor.
   *
   * A tap on a touch device synthesizes `mouseenter` but never a matching `mouseleave` —
   * the finger lifts, it doesn't move away. With `onMouseEnter` that left the tapped card
   * stuck showing its overlays forever while every other card, having never received a
   * `mouseenter`, looked dead. `pointerenter` fires before the synthesized mouse event and
   * carries `pointerType`, so excluding touch means a tap can no longer produce a hover state at
   * all — which is also what stops the two event families from both firing (see
   * .claude/rules/touch-mouse-events.md).
   *
   * Excluding touch rather than requiring a mouse, because a stylus hovers: a pen emits
   * compatibility mouse events, so `pointerType: 'pen'` used to reveal these overlays and a
   * mouse-only test would silently drop that.
   */
  const isHoveringPointer = (event: React.PointerEvent) => event.pointerType !== 'touch';

  const onPointerEnter = (event: React.PointerEvent) => {
    if (skip || !isHoveringPointer(event)) return;
    // If a fade-out from a recent leave is still pending, cancel it —
    // otherwise it would fire during this fresh hover and flip
    // `isHovered` to false while the cursor is still over the card.
    clearFadeOutTimer();
    setIsFadingOut(false);
    setIsHovered(true);
  };

  const onPointerLeave = (event: React.PointerEvent) => {
    if (skip || !isHoveringPointer(event)) return;
    setIsFadingOut(true);
    // Match transition duration — keep overlays visible while they fade.
    clearFadeOutTimer();
    fadeOutTimer.current = setTimeout(() => {
      setIsHovered(false);
      fadeOutTimer.current = null;
    }, 200);
  };

  /**
   * Force the card out of its hovered state immediately. Used after a
   * destructive modal (rename/delete) closes so the overlays don't
   * linger on a card the user just finished editing.
   *
   * Memoized with stable deps — setState setters and the fadeOutTimer
   * ref are all stable — so VideoMenuButton's effect that depends on
   * this callback doesn't see a fresh identity every VideoCard render.
   */
  const collapseOverlays = useCallback(() => {
    if (fadeOutTimer.current) {
      clearTimeout(fadeOutTimer.current);
      fadeOutTimer.current = null;
    }
    setIsFadingOut(false);
    setIsHovered(false);
  }, []);

  return {
    isHovered,
    isFadingOut,
    collapseOverlays,
    hoverHandlers: { onPointerEnter, onPointerLeave },
  };
}
